import { useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../client';
import { useAuthStore } from '../../store/authStore';
import { useChatStore } from '../../store/chatStore';
import { useFilterStore } from '../../store/filterStore';
import { useModalStore } from '../../store/modalStore';
import { useLocation } from 'react-router-dom';
import type { ChatContext, ConfirmationAction, StreamFrame } from '../../types/chat';

const WS_BASE_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/chat/ws`;
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;
const POLICY_VIOLATION_CLOSE_CODE = 1008;

const ROUTE_TO_PAGE: Record<string, string> = {
  '/approvals': '/reviews',
  '/records': '/reviews',
  '/': '/',
  '/reviews': '/reviews',
  '/ap-postings': '/reviews',
  '/upload': '/upload',
  '/admin/settings': '/admin/settings',
  '/org-structure': '/org-structure',
  '/approval-chain': '/approval-chain',
  '/notifications': '/notifications',
  '/policies': '/policies',
  '/memories': '/policies',
  '/vendors': '/vendors',
  '/profile': '/profile',
  '/vendor-profile': '/vendor-profile',
  '/vendor-customers': '/vendor-customers',
  '/vendor-invoices': '/vendor-invoices',
};


function buildChatContext(pathname: string, filters: Record<string, string[]>): ChatContext {
  const page = ROUTE_TO_PAGE[pathname] ?? pathname;

  // Detect entity from path
  let entityType: string | null = null;
  let entityId: string | null = null;

  const reviewMatch = pathname.match(/\/reviews\/(.+)/);
  const vendorEditMatch = pathname.match(/\/vendors\/(.+)\/edit/);

  if (reviewMatch) {
    entityType = 'document';
    entityId = reviewMatch[1];
  } else if (vendorEditMatch) {
    entityType = 'vendor';
    entityId = vendorEditMatch[1];
  }

  const flatFilters: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters)) {
    if (values.length > 0) {
      flatFilters[key] = values[0];
    }
  }

  const { activeModal, entityId: modalEntityId } = useModalStore.getState();

  return {
    page,
    entity_type: entityType,
    entity_id: entityId,
    filters: Object.keys(flatFilters).length > 0 ? flatFilters : null,
    active_modal: activeModal,
    modal_entity_id: modalEntityId,
  };
}


export function useChatWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAssistantOutputRef = useRef(false);
  const authRepairAttemptedRef = useRef(false);
  const pendingPayloadsRef = useRef<string[]>([]);

  const { isAuthenticated, currentUser, clearAuth } = useAuthStore();
  const canConnect = isAuthenticated && !!currentUser;
  const { addMessage, setPendingConfirmation } = useChatStore();
  const location = useLocation();
  const isChatWorkspace = location.pathname === '/chat';

  const handleMessage = useCallback(
    (data: StreamFrame) => {
      switch (data.type) {
        case 'tool_event': {
          if (data.status === 'executing') {
            addMessage({
              id: crypto.randomUUID(),
              type: 'tool_call',
              content: data.tool.replace(/_/g, ' '),
              timestamp: new Date(),
              toolCall: { tool: data.tool, status: 'executing' },
            });
          } else {
            useChatStore.getState().updateToolCallStatus(data.tool, data.status);
          }
          break;
        }

        case 'chunk': {
          const { streamingMessageId, startStreamingMessage, appendStreamChunk } =
            useChatStore.getState();
          if (!streamingMessageId) {
            startStreamingMessage();
          }
          appendStreamChunk(data.delta);
          if (data.delta?.trim()) {
            hasAssistantOutputRef.current = true;
          }
          break;
        }

        case 'confirmation': {
          const confirmation: ConfirmationAction = {
            action_id: data.action_id,
            tool_name: data.tool_name,
            tool_args: data.tool_args,
            summary: data.summary,
          };
          addMessage({
            id: crypto.randomUUID(),
            type: 'confirmation',
            content: `Please confirm: ${data.summary}`,
            timestamp: new Date(),
            confirmation,
          });
          hasAssistantOutputRef.current = true;
          setPendingConfirmation(confirmation);
          break;
        }

        case 'done': {
          const { finalizeStreamingMessage } = useChatStore.getState();
          finalizeStreamingMessage();
          if (data.platform_cards && data.platform_cards.length > 0) {
            const state = useChatStore.getState();
            state.addMessage({
              id: crypto.randomUUID(),
              type: 'action_result',
              content: '',
              timestamp: new Date(),
              platformCards: data.platform_cards,
            });
          }
          if (!hasAssistantOutputRef.current) {
            addMessage({
              id: crypto.randomUUID(),
              type: 'assistant',
              content: data.platform_cards?.length
                ? 'Here is the relevant platform information.'
                : 'I finished processing that, but did not receive a response to show.',
              timestamp: new Date(),
            });
          }
          hasAssistantOutputRef.current = false;
          break;
        }

        case 'error': {
          const { finalizeStreamingMessage } = useChatStore.getState();
          finalizeStreamingMessage();
          addMessage({
            id: crypto.randomUUID(),
            type: data.code === 'rate_limited' ? 'rate_limited' : 'error',
            content: data.content ?? 'Something went wrong.',
            timestamp: new Date(),
          });
          hasAssistantOutputRef.current = true;
          break;
        }
      }
    },
    [addMessage, setPendingConfirmation]
  );

  const connect = useCallback(() => {
    if (!canConnect || wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(WS_BASE_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0;
      authRepairAttemptedRef.current = false;
      const pendingPayloads = pendingPayloadsRef.current.splice(0);
      for (const payload of pendingPayloads) {
        ws.send(payload);
      }
    };

    ws.onmessage = (event) => {
      try {
        const data: StreamFrame = JSON.parse(event.data);
        handleMessage(data);
      } catch {
        // Ignore non-JSON messages
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      if (event.code === POLICY_VIOLATION_CLOSE_CODE) {
        if (!authRepairAttemptedRef.current) {
          authRepairAttemptedRef.current = true;
          void apiClient.get('/auth/me')
            .then(() => {
              reconnectTimer.current = setTimeout(connect, 100);
            })
            .catch(() => {
              pendingPayloadsRef.current = [];
              clearAuth();
              addMessage({
                id: crypto.randomUUID(),
                type: 'error',
                content: 'Chat session expired. Please sign in again.',
                timestamp: new Date(),
              });
            });
          return;
        }
        pendingPayloadsRef.current = [];
        clearAuth();
        addMessage({
          id: crypto.randomUUID(),
          type: 'error',
          content: 'Chat session expired. Please sign in again.',
          timestamp: new Date(),
        });
        return;
      }
      if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current += 1;
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [canConnect, handleMessage, addMessage, clearAuth]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    reconnectAttempts.current = 0;
  }, []);

  const sendMessage = useCallback(
    (message: string, sessionId?: string | null) => {
      hasAssistantOutputRef.current = false;

      addMessage({
        id: crypto.randomUUID(),
        type: 'user',
        content: message,
        timestamp: new Date(),
      });

      // Clear previous action chips on new input
      useChatStore.getState().clearActionChips();

      const { statusFilter, vendorFilter } = useFilterStore.getState();
      const filters: Record<string, string[]> = {};
      if (statusFilter.length > 0) filters.status = statusFilter;
      if (vendorFilter.length > 0) filters.vendor = vendorFilter;

      const context = buildChatContext(location.pathname, filters);
      if (isChatWorkspace) {
        context.page = '/chat';
        context.source = 'in_app';
      }
      const payload = JSON.stringify({
        message,
        context,
        session_id: sessionId ?? undefined,
      });

      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        pendingPayloadsRef.current.push(payload);
        addMessage({
          id: crypto.randomUUID(),
          type: 'error',
          content: 'Not connected to chat service. Trying to reconnect...',
          timestamp: new Date(),
        });
        connect();
        return;
      }

      wsRef.current.send(payload);
    },
    [location.pathname, addMessage, connect, isChatWorkspace]
  );

  // Always-on: connect when authenticated, disconnect on unmount
  useEffect(() => {
    if (canConnect) {
      connect();
    }
    return () => disconnect();
  }, [canConnect, connect, disconnect]);

  return { sendMessage, isConnected: wsRef.current?.readyState === WebSocket.OPEN };
}
